import { Fragment, useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronUp, Mail, Search, X } from 'lucide-react'

import { DEFAULT_FAQS } from '../../constants'
import { useMobileToolbarStore } from '@caradasstore/MobileToolbarStore'

const SUPPORT_EMAIL = 'support@adasintelligence.ai'

export const SupportPage = () => {
  const [searchInput, setSearchInput] = useState('')
  // Keyed by question, not list position, so the open answer stays put while searching (CAR-4020).
  const [openQ, setOpenQ] = useState(DEFAULT_FAQS[0]?.q) // first article expanded by default
  const searchOpen = useMobileToolbarStore((s) => s.searchOpen)
  const closeMobileToolbar = useMobileToolbarStore((s) => s.closeAll)
  useEffect(() => () => closeMobileToolbar(), [closeMobileToolbar])

  const totalCount = DEFAULT_FAQS.length
  const filteredFaqs = useMemo(() => {
    const q = searchInput.trim().toLowerCase()
    if (!q) return DEFAULT_FAQS
    return DEFAULT_FAQS.filter(
      (faq) => faq.q.toLowerCase().includes(q) || (faq.a || []).some((ans) => String(ans).toLowerCase().includes(q))
    )
  }, [searchInput])

  return (
    <div className='mx-auto max-w-[1400px] px-3 py-6 min-[435px]:px-0'>
      {/* Intro (desktop only — mobile uses the global toolbar title) */}
      <div className='hidden min-[435px]:block text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]'>Help Center</div>
      <h1 className='hidden min-[435px]:block mt-1 text-[28px] font-bold leading-tight tracking-tight text-[#0A1628]'>How can we help?</h1>
      <p className='hidden min-[435px]:block mt-2 max-w-[680px] text-[14px] text-[#6B7280]'>
        Answers to common questions about ADAS Intelligence. Can’t find yours? Email the support team.
      </p>

      {/* Mobile inline FAQ search */}
      {searchOpen && (
        <div className='min-[435px]:hidden mb-3 flex items-center gap-2 rounded-lg bg-[#F4F6F9] px-3 py-2.5'>
          <Search className='h-4 w-4 text-[#6B7280]' />
          <input
            type='text'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            autoFocus
            placeholder={`Search ${totalCount} answers`}
            className='flex-1 border-0 bg-transparent text-[14px] text-[#0A1628] placeholder:text-[#6B7280] focus:outline-none'
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              aria-label='Clear search'
              className='inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#475569]'
            >
              <X className='h-3 w-3' />
            </button>
          )}
        </div>
      )}

      {/* Contact-support featured card — stacks button below on mobile */}
      <div className='mt-2 min-[435px]:mt-5 overflow-hidden rounded-xl border border-[#EBEDF0] border-l-4 border-l-[#1F6FEB] bg-white'>
        <div className='flex flex-col gap-4 px-4 py-4 min-[435px]:flex-row min-[435px]:flex-wrap min-[435px]:items-center min-[435px]:justify-between min-[435px]:px-5'>
          <div className='flex min-w-0 items-start gap-4'>
            <div className='inline-flex h-12 w-12 flex-none items-center justify-center rounded-lg bg-[#E8EFFF]'>
              <Mail className='h-5 w-5 text-[#1F6FEB]' />
            </div>
            <div className='min-w-0'>
              <div className='text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]'>Contact support</div>
              <div className='mt-0.5 break-all text-[16px] font-bold text-[#0A1628]'>{SUPPORT_EMAIL}</div>
              <div className='mt-1 hidden min-[435px]:block text-[13px] text-[#6B7280]'>
                Include your location and the RO, quote or work order number.
              </div>
            </div>
          </div>
          <div className='flex flex-col items-stretch gap-1.5 min-[435px]:items-end'>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className='inline-flex items-center justify-center gap-2 rounded-lg bg-sidebar px-4 py-3 text-[14px] font-bold text-white transition-colors hover:bg-sidebar/90 min-[435px]:py-2 min-[435px]:text-[13px]'
            >
              <Mail className='h-4 w-4' />
              Email Support
            </a>
          </div>
        </div>
      </div>

      {/* FAQ card */}
      <div className='mt-3 min-[435px]:mt-5 rounded-xl border border-[#EBEDF0] bg-white p-4 min-[435px]:p-5'>
        {/* FAQ heading + search (desktop only) */}
        <div className='hidden min-[435px]:flex mb-4 flex-wrap items-start justify-between gap-3'>
          <div>
            <div className='text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]'>FAQ</div>
            <div className='mt-0.5 text-[18px] font-bold text-[#0A1628]'>Frequently asked questions</div>
          </div>
          {/* Search with the article count right-aligned beneath it */}
          <div className='w-full max-w-[320px]'>
            <div className='relative'>
              <Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]' />
              <input
                type='text'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={`Search ${totalCount} answers...`}
                className='h-10 w-full rounded-lg border border-[#D1D5D8] bg-white pl-9 pr-3 text-[13px] text-[#0A1628] placeholder-[#9CA3AF] focus:border-[#1F6FEB] focus:outline-none'
              />
            </div>
            <div className='mt-1.5 text-right text-[12px] text-[#6B7280]'>
              {searchInput ? (
                <>
                  Showing <span className='font-bold text-[#0A1628]'>{filteredFaqs.length}</span> of {totalCount} articles
                </>
              ) : (
                <>
                  Showing all <span className='font-bold text-[#0A1628]'>{totalCount}</span> articles
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mobile FAQ section eyebrow */}
        <div className='min-[435px]:hidden mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]'>
          Frequently asked questions
        </div>

        {filteredFaqs.length === 0 ? (
          <div className='rounded-lg border border-dashed border-[#D1D5D8] bg-[#F8FAFC] py-10 text-center text-[13px] text-[#6B7280]'>
            No articles match your search.
          </div>
        ) : (
          <div className='space-y-2'>
            {filteredFaqs.map((faq, idx) => {
              const isOpen = openQ === faq.q
              // A heading opens each run of a group; a group with no matches has no rows, so no heading.
              const startsGroup = faq.group && faq.group !== filteredFaqs[idx - 1]?.group
              return (
                <Fragment key={faq.q}>
                  {startsGroup && (
                    <h2 className={`${idx === 0 ? '' : 'pt-4 '}text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]`}>
                      {faq.group}
                    </h2>
                  )}
                  <div className='rounded-lg border border-[#EBEDF0] bg-white'>
                    <button
                      type='button'
                      onClick={() => setOpenQ(isOpen ? null : faq.q)}
                      aria-expanded={isOpen}
                      className='flex w-full items-center justify-between gap-3 px-4 py-3 text-left'
                    >
                      <span className='text-[14px] font-semibold text-[#0A1628]'>{faq.q}</span>
                      <span
                        className={`inline-flex h-7 w-7 flex-none items-center justify-center rounded-full ${
                          isOpen ? 'bg-[#0A1628] text-white' : 'bg-[#F1F4F8] text-[#6B7280]'
                        }`}
                      >
                        {isOpen ? <ChevronUp className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
                      </span>
                    </button>
                    {isOpen && (
                      <div className='space-y-2 border-t border-[#EBEDF0] px-4 py-3 text-[13px] leading-relaxed text-[#475569]'>
                        {(faq.a || []).map((ans, i) => (
                          <p key={i}>{ans}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </Fragment>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
